<template>
  <div class="system-message">
    <div class="message-toolbar">
      <el-radio-group v-model="queryParams.readStatus" size="mini" @change="handleFilterChange">
        <el-radio-button :label="null">全部</el-radio-button>
        <el-radio-button :label="0">未读</el-radio-button>
        <el-radio-button :label="1">已读</el-radio-button>
      </el-radio-group>
      <el-button
        type="primary"
        plain
        size="mini"
        :disabled="unreadCount <= 0"
        @click="handleReadAll"
      >
        全部已读
      </el-button>
    </div>

    <div v-loading="loading" class="message-list">
      <div v-if="messageList.length === 0" class="empty-wrapper">
        <el-empty description="暂无系统消息" :image-size="100" />
      </div>
      <div
        v-for="item in messageList"
        :key="item.id"
        class="message-item"
        :class="{ unread: isUnread(item) }"
      >
        <div class="message-header">
          <div class="message-title-wrap">
            <span class="message-title">{{ item.title }}</span>
            <el-tag v-if="isCompleted(item)" size="mini" type="success" effect="plain">已完成</el-tag>
            <el-tag v-if="isUnread(item)" size="mini" type="danger" effect="plain">未读</el-tag>
            <el-tag v-else-if="!isCompleted(item)" size="mini" type="info" effect="plain">已读</el-tag>
          </div>
          <div class="message-time-group">
            <span class="message-time">发送时间：{{ item.sendTime || '--' }}</span>
            <span v-if="!isUnread(item)" class="message-read-time">已读时间：{{ item.readTime || '--' }}</span>
            <span v-if="isCompleted(item)" class="message-complete-time">完成时间：{{ item.completeTime || '--' }}</span>
          </div>
        </div>
        <div class="message-content">{{ item.content }}</div>
        <div class="message-footer">
          <span class="message-type">{{ formatMessageType(item.messageType) }}</span>
          <div class="message-actions">
            <el-button
              v-if="isUnread(item)"
              type="text"
              size="mini"
              @click="handleRead(item)"
            >
              标记已读
            </el-button>
            <el-button
              v-if="canComplete(item)"
              type="text"
              size="mini"
              style="color: #67c23a;"
              @click="handleComplete(item)"
            >
              标记已完成
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <div class="message-pagination">
      <el-pagination
        background
        small
        layout="total, prev, pager, next"
        :current-page.sync="queryParams.pageNum"
        :page-size="queryParams.pageSize"
        :total="total"
        @current-change="loadMessages"
      />
    </div>
  </div>
</template>

<script>
import {
  listSystemMessage,
  getSystemMessageUnreadCount,
  markSystemMessageRead,
  markAllSystemMessageRead,
  completeSystemMessage
} from '@/api/system/message'

export default {
  name: 'SystemMessage',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      total: 0,
      unreadCount: 0,
      messageList: [],
      queryParams: {
        pageNum: 1,
        pageSize: 10,
        readStatus: null
      }
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        if (val) {
          this.loadMessages()
        }
      }
    }
  },
  methods: {
    loadMessages() {
      this.loading = true
      listSystemMessage(this.queryParams).then(res => {
        if (res.code === '200') {
          const list = res.data.list || []
          this.messageList = list.map(item => this.normalizeMessage(item))
          this.total = Number(res.data.total || 0)
        } else {
          this.messageList = []
          this.total = 0
        }
      }).finally(() => {
        this.loading = false
        this.loadUnreadCount()
      })
    },
    loadUnreadCount() {
      getSystemMessageUnreadCount().then(res => {
        this.unreadCount = res.code === '200' ? Number(res.data || 0) : 0
        this.$emit('refresh-count', this.unreadCount)
      })
    },
    handleFilterChange() {
      this.queryParams.pageNum = 1
      this.loadMessages()
    },
    normalizeMessage(item) {
      return {
        ...item,
        readStatus: Number(item.readStatus || 0),
        completeStatus: Number(item.completeStatus || 0)
      }
    },
    isUnread(item) {
      return Number(item.readStatus) === 0
    },
    isCompleted(item) {
      return Number(item.completeStatus) === 1
    },
    canComplete(item) {
      return item.messageType === 'OVERDUE_REMINDER' && !this.isCompleted(item)
    },
    handleRead(item) {
      markSystemMessageRead(item.id).then(res => {
        if (res.code === '200') {
          this.$message.success('已标记为已读')
          this.loadMessages()
          window.dispatchEvent(new Event('system-message-refresh'))
        }
      })
    },
    handleComplete(item) {
      completeSystemMessage(item.id).then(res => {
        if (res.code === '200') {
          this.$message.success('已完成该条超期提醒')
          this.loadMessages()
          window.dispatchEvent(new Event('system-message-refresh'))
        }
      })
    },
    handleReadAll() {
      markAllSystemMessageRead().then(res => {
        if (res.code === '200') {
          this.$message.success('已全部标记为已读')
          this.loadMessages()
          window.dispatchEvent(new Event('system-message-refresh'))
        }
      })
    },
    formatMessageType(type) {
      const typeMap = {
        INBOUND_REMINDER: '入库提醒',
        OVERDUE_REMINDER: '超期提醒'
      }
      return typeMap[type] || '系统提醒'
    }
  }
}
</script>

<style lang="scss" scoped>
.system-message {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.message-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.message-list {
  flex: 1;
  overflow-y: auto;
}

.message-item {
  padding: 14px 16px;
  margin-bottom: 12px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fff;
}

.message-item.unread {
  border-color: #c6e2ff;
  background: #f8fbff;
}

.message-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.message-title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.message-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.message-time {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
}

.message-time-group {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.message-read-time {
  font-size: 12px;
  color: #67c23a;
  white-space: nowrap;
}

.message-complete-time {
  font-size: 12px;
  color: #409eff;
  white-space: nowrap;
}

.message-content {
  font-size: 13px;
  line-height: 1.7;
  color: #606266;
  margin-bottom: 10px;
}

.message-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.message-type {
  font-size: 12px;
  color: #409eff;
}

.message-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.message-pagination {
  padding-top: 12px;
  text-align: right;
}

.empty-wrapper {
  padding-top: 40px;
}
</style>
